import { useMemo } from "react";
import {
  FaceIcon,
  RulerHorizontalIcon,
  CheckIcon,
  ChatBubbleIcon,
} from "@radix-ui/react-icons";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import { FieldFilter } from "@/v2/components/filter-menu/FieldFilter.model";
import { FilterMenu } from "@/v2/components/filter-menu/FilterMenu";
import {
  FilterMenuItemType,
  FilterMenuType,
} from "@/v2/components/filter-menu/FilterMenu.model";
import { categories } from "@/v2/components/formatters/CategoryFormatter";
import {
  exploreColumnTypeToLabel,
  isExploreColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  escalationReasons,
  ExploreInteractionType,
  resolvedReasons,
  resolutionStatuses,
  skipReasons,
} from "@/v2/entities/explore/Explore.model";
import {
  useExploreCount,
  useExploreValues,
} from "@/v2/entities/explore/Explore.api";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { exploreColumnTypeFormatters } from "@/v2/entities/explore/formatters/explore-formatters";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import {
  getResolutionStatusIcon,
  getResolutionStatusText,
} from "@/v2/entities/explore/formatters/ResolutionStatusFormatter";
import { getEscalationReasonText } from "@/v2/entities/explore/formatters/EscalationReasonFormatter";
import { getSkipReasonText } from "@/v2/entities/explore/formatters/SkipReasonFormatter";
import { getResolvedReasonText } from "@/v2/entities/explore/formatters/ResolvedReasonFormatter";
import { useExploreEnrichments } from "../explore/useExploreEnrichments";
import { useExploreCustomFields } from "../explore/useExploreCustomFields";
import { useExploreConversationCustomFieldsForView } from "../explore/useExploreConversationCustomFieldsForView";

const baseMenu: FilterMenuType<string> = [
  {
    field: "avgSentiment",
    kind: "single",
    label: exploreColumnTypeToLabel["avgSentiment"],
    icon: <FaceIcon />,
    options: [
      {
        label: "Positive",
        value: "positive",
      },
      {
        label: "Neutral",
        value: "neutral",
      },
      {
        label: "Negative",
        value: "negative",
      },
    ],
  },
  {
    field: "lastSentiment",
    kind: "single",
    label: exploreColumnTypeToLabel["lastSentiment"],
    icon: <FaceIcon />,
    options: [
      {
        label: "Positive",
        value: "positive",
      },
      {
        label: "Neutral",
        value: "neutral",
      },
      {
        label: "Negative",
        value: "negative",
      },
    ],
  },
  {
    field: "countPublicMessages",
    kind: "numeric",
    label: exploreColumnTypeToLabel["countPublicMessages"],
    icon: <ChatBubbleIcon />,
  },
  {
    field: "originAuthor",
    kind: "single",
    label: "Origin Ticket Author",
    options: [
      { label: "User", value: "USER" },
      { label: "Agent", value: "AGENT" },
    ],
  },
  {
    field: "isQuackAnswered",
    kind: "boolean",
    label: "Quack Answered",
    trueLabel: "Answered",
    falseLabel: "Not Answered",
  },
  {
    field: "isAnsweredByAui",
    kind: "boolean",
    label: exploreColumnTypeToLabel["isAnsweredByAui"],
    trueLabel: "Yes",
    falseLabel: "No",
  },
  {
    field: "validationStatus",
    kind: "boolean",
    label: exploreColumnTypeToLabel["validationStatus"],
    icon: <CheckIcon />,
    trueLabel: "Validated",
    falseLabel: "Pending",
  },
  {
    field: "category",
    kind: "multi",
    label: exploreColumnTypeToLabel["category"],
    options: categories.map((category) => ({
      value: category,
      label:
        exploreColumnTypeFormatters["category"]?.getText(category) ?? category,
      icon: exploreColumnTypeFormatters["category"]?.getIcon?.(category),
    })),
  },
  {
    field: "resolutionStatus",
    kind: "multi",
    label: "Resolution Status",
    options: resolutionStatuses.map((status) => ({
      value: status,
      label: getResolutionStatusText(status),
      icon: getResolutionStatusIcon(status),
    })),
  },
  {
    field: "escalationReason",
    kind: "multi",
    label: "Escalation Reason",
    options: escalationReasons.map((reason) => ({
      value: reason,
      label: getEscalationReasonText(reason),
    })),
  },
  {
    field: "skipReason",
    kind: "multi",
    label: "Skip Reason",
    options: skipReasons.map((reason) => ({
      value: reason,
      label: getSkipReasonText(reason),
    })),
  },
];

const newQaScoreMenu: FilterMenuType<string> = [
  {
    field: "avgQaScoreRoundRange",
    kind: "multi",
    label: exploreColumnTypeToLabel["avgQaScoreRoundRange"],
    icon: <RulerHorizontalIcon />,
    options: [
      {
        label: "0",
        value: "0",
      },
      {
        label: "1",
        value: "1",
      },
      {
        label: "2",
        value: "2",
      },
      {
        label: "3",
        value: "3",
      },
      {
        label: "4",
        value: "4",
      },
      {
        label: "5",
        value: "5",
      },
    ],
  },
];

const conversationMenu: FilterMenuType<string> = [
  {
    field: "avgQuackMessageScoreRoundRange",
    kind: "multi",
    label: exploreColumnTypeToLabel["avgQuackMessageScoreRoundRange"],
    icon: <RulerHorizontalIcon />,
    options: [
      {
        label: "No Score",
        value: "-1",
      },
      {
        label: "0",
        value: "0",
      },
      {
        label: "1",
        value: "1",
      },
      {
        label: "2",
        value: "2",
      },
      {
        label: "3",
        value: "3",
      },
      {
        label: "4",
        value: "4",
      },
      {
        label: "5",
        value: "5",
      },
    ],
  },
  {
    field: "resolvedReason",
    kind: "multi",
    label: "Resolved Reason",
    options: resolvedReasons.map((reason) => ({
      value: reason,
      label: getResolvedReasonText(reason),
    })),
  },
  {
    field: "isUserAuthenticated",
    kind: "boolean",
    label: "Is User Authenticated",
    trueLabel: "Yes",
    falseLabel: "No",
  },
];

export type ExploreFiltersValue = Partial<Record<string, FieldFilter>>;

interface FilterMenuProps {
  source?: ExploreInteractionType;
  value: ExploreFiltersValue;
  size?: "1" | "2" | "3";
  label?: string;
  onChange: (value: ExploreFiltersValue) => void;
  onClearAll?: () => void;
  filter?: Filter;
  allowedFields?: string[];
  viewOnly?: boolean;
  hideCount?: boolean;
}

export function ExploreFilterMenu({
  source,
  value,
  size,
  label = "Filter",
  onChange,
  onClearAll,
  filter,
  allowedFields,
  viewOnly,
  hideCount = false,
}: FilterMenuProps) {
  const { data: ticketValues } = useExploreValues({
    source,
  });

  const { data, isLoading } = useExploreCount({
    source,
    filter,
  });

  const enrichments = useExploreEnrichments();
  const customFields = useExploreCustomFields();
  const conversationCustomFieldsForView =
    useExploreConversationCustomFieldsForView();
  const customFieldsBySource =
    source === "conversations" ? conversationCustomFieldsForView : customFields;

  const { data: config } = useQuackchatPlatformConfiguration();
  const csatType = config?.chatSettings?.csatType;

  const csatMenu: FilterMenuItemType<"csat"> = useMemo(() => {
    const isBinary = csatType === "binary";

    return {
      field: "csat",
      kind: "multi",
      label: "CSAT",
      options: isBinary
        ? [
            { label: "1", value: "1" },
            { label: "5", value: "5" },
          ]
        : [
            { label: "1", value: "1" },
            { label: "2", value: "2" },
            { label: "3", value: "3" },
            { label: "4", value: "4" },
            { label: "5", value: "5" },
          ],
    };
  }, [csatType]);

  const dynamicMenu = useMemo(() => {
    const menu = [
      ...newQaScoreMenu,
      csatMenu,
      ...baseMenu,
      ...(source === "conversations" ? conversationMenu : []),
    ];

    if (ticketValues == null) {
      return menu;
    }

    const dynamicMenu: FilterMenuType<string> = [];

    for (const [field, values] of Object.entries(ticketValues)) {
      if (values.length === 0) {
        continue;
      }

      if (menu.some((item) => item.field === field)) {
        continue;
      }

      const kind = values.length > 100 ? "search" : "multi";

      if (isExploreColumnType(field)) {
        if (field === "team") {
          dynamicMenu.push({
            field,
            kind: "multi",
            label: exploreColumnTypeToLabel[field],
            searchable: true,
            options: values.map((value) => ({
              value,
              label:
                exploreColumnTypeFormatters[field]?.getText(value) ?? value,
              icon: exploreColumnTypeFormatters[field]?.getIcon?.(value),
            })),
          });
        } else if (field === "channel") {
          const conversationChannels = values.filter(
            (value) => value === "autonomous_quackchat" || value === "slackbot"
          );

          const ticketChannels = values.filter(
            (value) => value !== "autonomous_quackchat" && value !== "slackbot"
          );

          dynamicMenu.push({
            field,
            kind,
            label: exploreColumnTypeToLabel[field],
            options: [
              {
                label: "Tickets",
                options: ticketChannels.map((value) => ({
                  value,
                  label:
                    exploreColumnTypeFormatters[field]?.getText(value) ?? value,
                  icon: exploreColumnTypeFormatters[field]?.getIcon?.(value),
                })),
              },
              {
                label: "Conversations",
                options: conversationChannels.map((value) => ({
                  value,
                  label:
                    exploreColumnTypeFormatters[field]?.getText(value) ?? value,
                  icon: exploreColumnTypeFormatters[field]?.getIcon?.(value),
                })),
              },
            ],
          });
        } else {
          dynamicMenu.push({
            field,
            kind,
            label: exploreColumnTypeToLabel[field],
            options: values.map((value) => ({
              value,
              label:
                exploreColumnTypeFormatters[field]?.getText(value) ?? value,
              icon: exploreColumnTypeFormatters[field]?.getIcon?.(value),
            })),
          });
        }

        continue;
      }
    }

    for (const enrichment of enrichments) {
      if (enrichment.options.length === 0) {
        continue;
      }

      if (enrichment.options.length > 100) {
        dynamicMenu.push({
          field: enrichment.value,
          kind: "search",
          label: enrichment.label,
        });
        continue;
      }

      dynamicMenu.push({
        field: enrichment.value,
        kind: "multi",
        label: enrichment.label,
        options: enrichment.options.map((option) => ({
          label: option.name,
          value: option.value,
        })),
      });
    }

    for (const customField of customFieldsBySource) {
      if (customField.options.length === 0) {
        continue;
      }

      dynamicMenu.push({
        field: customField.label,
        kind: "multi",
        label: customField.label,
        options: customField.options.map((option) => ({
          label: option.name,
          value: option.value,
        })),
      });
      continue;
    }

    if (allowedFields && allowedFields.length > 0) {
      return [...menu, ...dynamicMenu].filter((item) =>
        allowedFields.includes(item.field)
      );
    }

    return [...menu, ...dynamicMenu];
  }, [
    ticketValues,
    allowedFields,
    enrichments,
    csatMenu,
    customFieldsBySource,
    source,
  ]);

  return (
    <FilterMenu
      size={size}
      menu={dynamicMenu}
      value={value}
      onChange={onChange}
      label={label}
      count={data?.count}
      onClearAll={onClearAll}
      loading={isLoading}
      viewOnly={viewOnly}
      hideCount={hideCount}
    />
  );
}
